import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { fetchAppConfig } from "@/lib/appConfig";
import { computeTierStyle } from "@/lib/tierStyle";
import {
  ArrowDownLeft, ArrowUpRight, Loader2, Crown, Medal, Trophy, Clock, Calendar
} from "lucide-react";

const TIER_ICON = { Silver: Trophy, Gold: Trophy, Platinum: Trophy };
// Metallic tile palette for the 3 Points-Summary tiles — text colour matches the digital card (#0A2540)
const TIER_TILE = {
  Silver:   { bg: "linear-gradient(145deg, #F4F7FA 0%, #B6C2D0 55%, #6F7A8A 100%)", text: "text-[#0A2540]",            sub: "text-[#0A2540]/70", ring: "ring-[#6F7A8A]/30" },
  Gold:     { bg: "linear-gradient(145deg, #FBE38B 0%, #D4A93A 55%, #8C6618 100%)", text: "text-[#0A2540]",            sub: "text-[#0A2540]/70", ring: "ring-[#8C6618]/40" },
  Platinum: { bg: "linear-gradient(145deg, #2B50B0 0%, #173075 55%, #0A1736 100%)", text: "text-white",                sub: "text-white/70", ring: "ring-[#0A1736]/40" },
};

const RANGE_OPTIONS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "custom", label: "Custom" },
];

function rangeToDates(range, customFrom, customTo) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "today") return { from: startOfToday, to: now };
  if (range === "7d") return { from: new Date(now.getTime() - 7 * 86400000), to: now };
  if (range === "30d") return { from: new Date(now.getTime() - 30 * 86400000), to: now };
  if (range === "custom" && customFrom && customTo) {
    return { from: new Date(customFrom), to: new Date(`${customTo}T23:59:59`) };
  }
  return { from: null, to: null };
}

export default function PointsLedger() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [appCfg, setAppCfg] = useState(null);
  useEffect(() => { fetchAppConfig().then(setAppCfg); }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const typeParam = tab === "all" ? "" : `&type=${tab}`;
      const [balRes, txnRes] = await Promise.all([
        API.get("/points/balance"),
        API.get(`/points/history?page=${page}&limit=50${typeParam}`),
      ]);
      setBalance(balRes.data);
      setTransactions(txnRes.data.transactions || []);
      setTotalPages(txnRes.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [tab, page]);

  useEffect(() => { loadData(); }, [loadData]);

  const tier = user?.tier || "Silver";
  const totalEarned = balance?.total_points_earned || 0;
  const totalBurned = balance?.total_points_burned || 0;
  const balancePoints = balance?.balance_points || 0;
  // Point 38 + 40: tier name, thresholds, progress and card colour all come from
  // the admin config so this screen matches the Home card exactly.
  const ts = computeTierStyle(tier, totalEarned, appCfg);
  const TierIcon = TIER_ICON[ts.tierName] || TIER_ICON[tier] || Trophy;
  const tile = TIER_TILE[ts.tierName] || TIER_TILE[tier] || TIER_TILE.Silver;
  const nextTier = ts.nextTierName;
  const pointsToNext = ts.pointsToNext;
  const progressPercent = ts.progressPercent;

  // Apply date range filter to transactions for display only
  const filteredTxns = useMemo(() => {
    const { from, to } = rangeToDates(range, customFrom, customTo);
    if (!from || !to) return transactions;
    return transactions.filter(t => {
      const d = new Date(t.created_at);
      return d >= from && d <= to;
    });
  }, [transactions, range, customFrom, customTo]);

  const txnColor = (type) => {
    if (type === "earn") return { bg: "bg-emerald-50", text: "text-emerald-600", icon: ArrowDownLeft, sign: "+" };
    if (type === "burn") return { bg: "bg-red-50", text: "text-red-500", icon: ArrowUpRight, sign: "-" };
    return { bg: "bg-blue-50", text: "text-blue-500", icon: Clock, sign: "" };
  };

  return (
    <div className="space-y-5 animate-slide-up" data-testid="points-ledger">
      {/* Digital Card — mirrors the Home tier card surface (Point 40) */}
      {(() => {
        const isDarkTier = ts.isDark;
        const txtMain = isDarkTier ? "text-white" : "text-[#0A2540]";
        const txtMute = isDarkTier ? "text-white/75" : "text-[#0A2540]/70";
        const tierChipBg = isDarkTier ? "bg-white/20" : "bg-[#0A2540]/10";
        const txtShadow = isDarkTier ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" : "drop-shadow-[0_1px_1px_rgba(255,255,255,0.35)]";
        const cardBgClass = ts.customImg ? "" : `bg-gradient-to-br ${ts.gradientClass}`;
        const cardBgStyle = ts.customImg
          ? { backgroundImage: `url(${ts.customImg})`, backgroundSize: "cover", backgroundPosition: "center" }
          : undefined;
        return (
          <div className={`relative rounded-2xl overflow-hidden ${cardBgClass} p-5 shadow-lg`} style={cardBgStyle} data-testid="digital-card">
            {ts.customImg && <div aria-hidden="true" className="absolute inset-0 bg-black/35" />}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10" />
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-6 -mb-6" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
            <div className="relative flex items-start justify-between">
              <div>
                <p className={`text-[10px] tracking-[0.18em] font-semibold ${txtMute}`}>MyErba Rewards</p>
                <h2 className={`text-lg font-bold mt-1 ${txtMain} ${txtShadow}`}>{user?.full_name || "Member"}</h2>
                <p className={`text-xs mt-0.5 ${txtMute}`}>{user?.mobile}</p>
              </div>
              <div className={`flex items-center gap-1.5 ${tierChipBg} px-3 py-1.5 rounded-full backdrop-blur-sm ring-1 ring-white/20`}>
                <TierIcon className={`w-4 h-4 ${txtMain}`} /><span className={`text-xs font-bold ${txtMain}`} data-testid="digital-card-tier">{ts.tierName}</span>
              </div>
            </div>
            <div className="relative mt-4 flex items-end justify-between">
              <div>
                <p className={`text-[10px] tracking-wide ${txtMute}`}>Available points</p>
                <p className={`text-3xl font-black ${txtMain} ${txtShadow}`}>{balancePoints.toLocaleString()}</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tier Progress — tier names & thresholds from admin config (Point 38) */}
      <div className="bg-white rounded-2xl border p-4" data-testid="tier-progress">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Tier Progress</h3>
        <div className="relative">
          <div className="flex justify-between mb-1">
            {ts.steps.map((s, i) => {
              const TIcon = TIER_ICON[s.name] || Trophy;
              const isActive = ts.curIndex >= i;
              const isCurrent = s.name === ts.tierName;
              return (
                <div key={s.name} className={`flex flex-col items-center ${isActive ? "opacity-100" : "opacity-40"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isActive ? (isCurrent ? "bg-[#008A99] border-[#008A99] text-white" : "bg-white border-[#008A99] text-[#008A99]") : "bg-gray-100 border-gray-200 text-gray-400"}`}>
                    <TIcon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] font-semibold mt-1 ${isCurrent ? "text-[#008A99]" : "text-gray-400"}`}>{s.name}</span>
                  <span className="text-[8px] text-gray-400">{(s.min || 0).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
          <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mt-2">
            <div className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#008A99] to-[#008A99] transition-all duration-700" style={{ width: `${progressPercent}%` }} />
          </div>
          {nextTier && (
            <p className="text-center text-xs text-[#008A99] font-semibold mt-2">
              {pointsToNext.toLocaleString()} points to reach {nextTier}
            </p>
          )}
        </div>
      </div>

      {/* Points Summary — 3 metallic tiles matching the user's current slab */}
      <div className="bg-white rounded-2xl border p-4" data-testid="points-summary">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Points Summary</h3>
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => setTab("earn")} className={`rounded-xl p-3 text-left hover:ring-2 ${tile.ring} transition-all relative overflow-hidden`} style={{ background: tile.bg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 10px rgba(0,0,0,0.12)" }} data-testid="summary-earned">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
            <p className={`text-[10px] font-bold tracking-wide ${tile.sub} relative`}>Lifetime earned</p>
            <p className={`text-xl font-black mt-1 ${tile.text} relative`}>{totalEarned.toLocaleString()}</p>
          </button>
          <button onClick={() => setTab("burn")} className={`rounded-xl p-3 text-left hover:ring-2 ${tile.ring} transition-all relative overflow-hidden`} style={{ background: tile.bg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 10px rgba(0,0,0,0.12)" }} data-testid="summary-redeemed">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
            <p className={`text-[10px] font-bold tracking-wide ${tile.sub} relative`}>Lifetime redeemed</p>
            <p className={`text-xl font-black mt-1 ${tile.text} relative`}>{totalBurned.toLocaleString()}</p>
          </button>
          <button onClick={() => setTab("all")} className={`rounded-xl p-3 text-left hover:ring-2 ${tile.ring} transition-all relative overflow-hidden`} style={{ background: tile.bg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 10px rgba(0,0,0,0.12)" }} data-testid="summary-balance">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
            <p className={`text-[10px] font-bold tracking-wide ${tile.sub} relative`}>Balance</p>
            <p className={`text-xl font-black mt-1 ${tile.text} relative`}>{balancePoints.toLocaleString()}</p>
          </button>
        </div>
      </div>

      {/* Transaction History with Date Range Filter */}
      <div data-testid="transaction-history">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">Transaction History</h3>
          <span className="text-[10px] text-gray-400">{filteredTxns.length} entries</span>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-2xl border p-3 mb-3 space-y-2" data-testid="date-range-filter">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Calendar className="w-3.5 h-3.5 text-[#008A99]" />
            <span className="font-semibold">Filter by date</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {RANGE_OPTIONS.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${range === r.key ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {range === "custom" && (
            <div className="pt-2 mt-1 border-t border-gray-100" data-testid="custom-date-block">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">From</span>
                  <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:border-[#008A99] focus:ring-2 focus:ring-[#008A99]/15"
                    data-testid="custom-from" />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">To</span>
                  <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:border-[#008A99] focus:ring-2 focus:ring-[#008A99]/15"
                    data-testid="custom-to" />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Type filter chips */}
        <div className="flex gap-2 mb-3">
          {[
            { key: "all", label: "All", color: "bg-gray-100 text-gray-700" },
            { key: "earn", label: "Earned", color: "bg-emerald-100 text-emerald-700" },
            { key: "burn", label: "Redeemed", color: "bg-red-100 text-red-700" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => { setTab(f.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab === f.key ? f.color : "bg-gray-50 text-gray-400"}`}
              data-testid={`txn-tab-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
        ) : filteredTxns.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border"><p className="text-gray-400 text-sm">No transactions in selected range</p></div>
        ) : (
          <div className="bg-white rounded-2xl border divide-y divide-gray-50">
            {filteredTxns.map((txn, i) => {
              const color = txnColor(txn.type);
              const TxnIcon = color.icon;
              return (
                <div key={i} className="px-4 py-3 flex items-center justify-between" data-testid={`txn-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color.bg}`}>
                      <TxnIcon className={`w-4 h-4 ${color.text}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{txn.description}</p>
                      <p className="text-[10px] text-gray-400">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${color.text}`}>
                    {color.sign}{txn.points}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-30">Prev</button>
            <span className="px-3 py-1.5 text-sm text-gray-500">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-30">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
